/**
 * Chat Assistant — React bindings.
 *
 * The socket is owned by the application, not by any one screen:
 * `ChatAssistantBoot` opens it once after sign-in and it stays open for the
 * session, so a reply that is still streaming survives navigating away from the
 * chat screen and back.
 */

import { useEffect } from "react";
import { useChatAssistantStore } from "./store";
import { isChatAssistantConfigured } from "./config";
import type { ChatMessage } from "./types";

/**
 * Open the connection for the lifetime of the signed-in session.
 *
 * Deliberately has no teardown: unmounting a screen must not drop the socket.
 * The session itself is what ends it — the store closes and clears everything on
 * the Cognito `signedOut` event.
 */
export function useChatAssistantConnection(): void {
    const connect = useChatAssistantStore((state) => state.connect);

    useEffect(() => {
        if (!isChatAssistantConfigured()) return;
        connect();
    }, [connect]);
}

/**
 * Mount once, high in the tree, inside the authenticated area. Renders nothing;
 * it exists so the connection's lifetime is tied to the app rather than to
 * whichever screen happens to be showing.
 */
export function ChatAssistantBoot(): null {
    useChatAssistantConnection();
    return null;
}

/** State and actions for the screen that renders the chat. */
export function useChatAssistant() {
    const state = useChatAssistantStore();

    useChatAssistantConnection();

    return {
        ...state,
        isConfigured: isChatAssistantConfigured(),
    };
}

/** Messages of the current conversation, without subscribing to the rest. */
export function useChatMessages(): ChatMessage[] {
    return useChatAssistantStore((state) => state.messages);
}
